import { useState, useEffect } from "react";
import { getStagesForOrder, getStageIndex } from "../data/orderStages";
import styles from "./TrackOrder.module.css";

const TRACK_API = "https://ratnapura-gems-api.vercel.app/api/track";

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return null;
  }
}

export default function TrackOrder() {
  const [trackingId, setTrackingId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Support deep links like /track?id=RG-8F3K2A
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    if (id) {
      setTrackingId(id);
      lookup(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lookup = async (id) => {
    // Be forgiving: customers paste codes with spaces, add dashes from
    // memory, or type in lowercase. Strip anything that isn't a letter
    // or digit and uppercase the rest.
    const query = (id ?? trackingId).replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (!query) {
      setError("Please enter your tracking number.");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const r = await fetch(`${TRACK_API}?id=${encodeURIComponent(query)}`);
      const data = await r.json();
      if (!data.success) {
        setError(data.error || "We couldn't find that order.");
      } else {
        setResult(data);
      }
    } catch {
      setError("Couldn't reach our servers. Please try again in a moment.");
    }
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    lookup();
  };

  const hasCertificate = result?.order?.hasCertificate ?? true;
  const stages = result ? getStagesForOrder(hasCertificate) : [];
  const currentIndex = result ? getStageIndex(result.order.stage, hasCertificate) : -1;

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <button className={styles.backBtn} onClick={() => window.history.back()}>← Back</button>
        <p className={styles.eyebrow}>Ratnapura Gems</p>
        <h1 className={styles.title}>Track Your Order</h1>
        <p className={styles.subtitle}>
          Enter the tracking number we sent you to follow your piece from gemstone to doorstep.
        </p>
      </div>

      <div className={styles.content}>
        <form className={styles.searchCard} onSubmit={handleSubmit}>
          <input
            className={styles.input}
            placeholder="e.g. 8F3K2A"
            value={trackingId}
            onChange={(e) => { setTrackingId(e.target.value); setError(""); }}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
            inputMode="text"
            maxLength={12}
          />
          <button type="submit" className={styles.searchBtn} disabled={loading}>
            {loading ? "Searching…" : "Track"}
          </button>
        </form>

        {error && <p className={styles.errorMsg}>{error}</p>}

        {result && (
          <>
            {/* Summary */}
            <div className={styles.summaryCard}>
              <div className={styles.summaryRow}>
                <span className={styles.summaryKey}>Tracking Number</span>
                <span className={styles.summaryValue}>{result.order.trackingId}</span>
              </div>
              {result.order.productName && (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryKey}>Item</span>
                  <span className={styles.summaryValue}>{result.order.productName}</span>
                </div>
              )}
              {formatDate(result.order.orderedAt) && (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryKey}>Ordered</span>
                  <span className={styles.summaryValue}>{formatDate(result.order.orderedAt)}</span>
                </div>
              )}
            </div>

            {/* Internal journey */}
            <div className={styles.timelineCard}>
              <h2 className={styles.sectionTitle}>Order Progress</h2>
              <div className={styles.timeline}>
                {stages.map((stage, i) => {
                  const done = i < currentIndex;
                  const active = i === currentIndex;
                  // Only show a date for stages already reached. Without this,
                  // an order moved back to an earlier stage would still show
                  // timestamps on later stages from its previous progress.
                  const stamp = i <= currentIndex
                    ? formatDate(result.order.stageHistory?.[stage.key])
                    : null;
                  return (
                    <div
                      key={stage.key}
                      className={`${styles.step} ${done ? styles.stepDone : ""} ${active ? styles.stepActive : ""}`}
                    >
                      <div className={styles.stepMarker}>
                        <span className={styles.stepIcon}>{done ? "✓" : stage.icon}</span>
                        {i < stages.length - 1 && <span className={styles.stepLine} />}
                      </div>
                      <div className={styles.stepBody}>
                        <p className={styles.stepLabel}>{stage.label}</p>
                        <p className={styles.stepDesc}>{stage.description}</p>
                        {stamp && <p className={styles.stepDate}>{stamp}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Courier checkpoints */}
            {result.courierCheckpoints && result.courierCheckpoints.length > 0 && (
              <div className={styles.timelineCard}>
                <div className={styles.courierHeader}>
                  <h2 className={styles.sectionTitle}>Courier Updates</h2>
                  <span className={styles.courierBadge}>Pronto · {result.order.prontoId}</span>
                </div>
                <div className={styles.courierList}>
                  {result.courierCheckpoints.map((cp, i) => (
                    <div key={i} className={`${styles.courierItem} ${i === 0 ? styles.courierLatest : ""}`}>
                      <div className={styles.courierDot} />
                      <div>
                        <p className={styles.courierDesc}>{cp.description}</p>
                        <p className={styles.courierMeta}>
                          {cp.creation}{cp.location ? ` · ${cp.location}` : ""}
                        </p>
                        {cp.pod_url && (
                          <a href={cp.pod_url} target="_blank" rel="noopener noreferrer" className={styles.podLink}>
                            View delivery proof ↗
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.order.stage === "shipped" && !result.courierCheckpoints && (
              <p className={styles.courierPending}>
                Handed to the courier — live tracking updates will appear here shortly.
              </p>
            )}
          </>
        )}

        {/* Help */}
        <div className={styles.helpBox}>
          <h3 className={styles.helpTitle}>Can't find your order?</h3>
          <p className={styles.helpText}>
            Message us with your order details and we'll look it up for you right away.
          </p>
          <a
            href="https://wa.me/94765851400?text=Hi!%20I%20need%20help%20tracking%20my%20order."
            target="_blank"
            rel="noopener noreferrer"
            className={styles.helpBtn}
          >
            <img src="/whatsapp-logo.png" alt="WhatsApp" width={18} height={18} />
            Ask Us on WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}